import { memo } from "react";
import {
  Alert,
  Collapse,
  Descriptions,
  message,
  Modal,
  Row,
  Switch,
} from "antd";
import { useMsal } from "@azure/msal-react";

import { IPond } from "pages/Ponds/types";
import {
  enableObjectLock,
  enableReplication,
  disableReplication,
} from "http/userApi";
import Tooltip from "common/Tooltip";
import { useLogin } from "hooks/useLogin";

import { ObjectLockTooltip } from "components/General/components/ObjectLockTooltip";
import { ReplicationTooltip } from "components/General/components/ReplicationTooltip";

const REPLICATION = "Replication";

const labelStyle = {
  width: 180,
  color: "#8c8c8c",
  alignSelf: "center",
};

const AdvancedSettings = ({
  bucket,
  onFetchBucket,
  loading,
  onSetLoading,
}: {
  bucket: IPond;
  onFetchBucket: () => Promise<unknown>;
  loading: boolean;
  onSetLoading: (value: boolean) => void;
}) => {
  const msalInstance = useMsal();
  const { showAuthorizationErrorModal } = useLogin(msalInstance);

  const handleObjectLock = () => {
    Modal.confirm({
      title: "Enable Object Lock",
      content: (
        <>
          Enabling Object Lock makes it available on this bucket, allowing
          writers and owners to apply WORM (Write Once, Read Many) retention to
          objects. Enabling it does not lock any objects by itself. This setting
          is <strong>irreversible</strong> — once enabled, Object Lock cannot be
          disabled. Are you sure you want to continue?
        </>
      ),
      okText: "Enable",
      cancelText: "Cancel",
      onOk: async () => {
        onSetLoading(true);

        const result: {
          data: string | null;
          error: null | string;
        } = await enableObjectLock(bucket.name, msalInstance);

        if (result.error === "token expired") {
          onSetLoading(false);
          return showAuthorizationErrorModal();
        }

        if (result.data) {
          await onFetchBucket();
          message.success({
            content: "Object Lock has been enabled",
            duration: 2,
          });
        }

        if (result.error) {
          message.error({
            content: "Something went wrong. Try reloading",
            duration: 2,
          });
        }

        onSetLoading(false);
      },
    });
  };

  const handleReplicationSwitch = async (checked: boolean) => {
    onSetLoading(true);

    const action = checked ? enableReplication : disableReplication;

    const result: {
      data: string | null;
      error: null | string;
    } = await action(bucket.name, msalInstance);

    if (result.error === "token expired") {
      onSetLoading(false);
      return showAuthorizationErrorModal();
    }

    if (result.data) {
      await onFetchBucket();
      message.success({
        content: `${REPLICATION} has been changed`,
        duration: 2,
      });
    }

    if (result.error) {
      message.error({
        content: "Something went wrong. Try reloading",
        duration: 2,
      });
    }

    onSetLoading(false);
  };

  return (
    <Collapse className="pond-page-card" defaultActiveKey={[]}>
      <Collapse.Panel header="Advanced settings" key="advanced">
        <Alert
          type="warning"
          showIcon
          message="Please make sure you understand the effect of these settings before making changes."
          style={{ marginBottom: 16 }}
        />
        <Row>
          <Descriptions column={2}>
            <Descriptions.Item
              label={
                <Tooltip title="Object Lock" tooltip={<ObjectLockTooltip />} />
              }
              labelStyle={labelStyle}
              className="descr-item"
            >
              <Switch
                checked={bucket.params.object_lock?.enable ?? false}
                loading={loading}
                onChange={handleObjectLock}
                disabled={
                  !bucket.params.object_lock ||
                  bucket.params.object_lock.enable ||
                  !bucket.management_permissions.can_toggle_object_lock
                }
              />
            </Descriptions.Item>
          </Descriptions>
        </Row>
        <Row>
          <Descriptions column={2}>
            <Descriptions.Item
              label={
                <Tooltip title="Replication" tooltip={<ReplicationTooltip />} />
              }
              labelStyle={labelStyle}
              className="descr-item"
            >
              <Switch
                checked={bucket.params.replication?.enable ?? true}
                loading={loading}
                onChange={handleReplicationSwitch}
                disabled={!bucket.management_permissions.can_toggle_replication}
              />
            </Descriptions.Item>
          </Descriptions>
        </Row>
      </Collapse.Panel>
    </Collapse>
  );
};

export default memo(AdvancedSettings);
